'use client';

import { c as _c } from "react/compiler-runtime";
import { Check } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { toast } from 'sonner';
import { PrV2CopyNumberButton } from './pr-v2-copy-number-button';
import { Input } from '@/components/ui/input';
import { usePrV2UpdateTitle } from '@/data/v2/pr/use-pr-v2-update-title';
import { cn } from '@/lib/utils';
type PrV2TitleProps = {
  owner: string;
  repo: string;
  number: string;
  prNumber: number;
  title: string | null;
  readOnly?: boolean;
  compact?: boolean;
};

// Keep the same row geometry for view/edit mode to avoid title jump.
const TITLE_ROW_STYLE = 'flex w-full gap-1 rounded-md px-1 py-0.5 text-left font-semibold text-foreground';
export function PrV2Title(t0) {
  const $ = _c(49);
  const {
    owner,
    repo,
    number,
    prNumber,
    title,
    readOnly: t1,
    compact: t2
  } = t0;
  const readOnly = t1 === undefined ? false : t1;
  const compact = t2 === undefined ? false : t2;
  const [isEditing, setIsEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(title ?? "");
  const [optimisticTitle, setOptimisticTitle] = useState(null);
  const [prevTitle, setPrevTitle] = useState(title);
  const titleMutation = usePrV2UpdateTitle(owner, repo, number);
  const inputRef = useRef(null);
  const ignoreNextBlurRef = useRef(false);
  const numberTextId = useId();
  const titleTextId = useId();
  if (prevTitle !== title) {
    setPrevTitle(title);
    if (optimisticTitle !== null && title === optimisticTitle) {
      setOptimisticTitle(null);
    }
  }
  const displayedTitle = optimisticTitle ?? title ?? "";
  const titleLabel = displayedTitle || "Untitled pull request";
  const headingLabelledBy = `${numberTextId} ${titleTextId}`;
  const titleTypeClassName = compact ? "text-base leading-snug" : "text-xl leading-tight";
  const rowClassName = cn(TITLE_ROW_STYLE, titleTypeClassName, compact ? "min-h-8 gap-1.5" : "min-h-[36px]");
  let t3;
  let t4;
  if ($[0] !== isEditing) {
    t3 = () => {
      if (!isEditing) {
        return;
      }
      const input = inputRef.current;
      if (!input) {
        return;
      }
      input.focus();
      input.setSelectionRange(input.value.length, input.value.length);
    };
    t4 = [isEditing];
    $[0] = isEditing;
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  let t5;
  if ($[3] !== displayedTitle) {
    t5 = () => {
      ignoreNextBlurRef.current = false;
      setDraftTitle(displayedTitle);
      setIsEditing(true);
    };
    $[3] = displayedTitle;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const onTitleClick = t5;
  let t6;
  if ($[5] !== displayedTitle) {
    t6 = () => {
      setDraftTitle(displayedTitle);
      setIsEditing(false);
    };
    $[5] = displayedTitle;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const cancelEditing = t6;
  let t7;
  if ($[7] !== cancelEditing || $[8] !== displayedTitle || $[9] !== draftTitle || $[10] !== titleMutation) {
    t7 = t8 => {
      const source = t8 === undefined ? "explicit" : t8;
      if (titleMutation.isPending) {
        return;
      }
      const currentTitle = displayedTitle.trim();
      const normalizedTitle = draftTitle.trim();
      if (normalizedTitle === currentTitle) {
        setIsEditing(false);
        return;
      }
      if (!normalizedTitle) {
        if (source === "blur") {
          cancelEditing();
          return;
        }
        toast.error("Title cannot be empty");
        queueMicrotask(() => inputRef.current?.focus());
        return;
      }
      setOptimisticTitle(normalizedTitle);
      setDraftTitle(normalizedTitle);
      setIsEditing(false);
      titleMutation.mutate(normalizedTitle, {
        onSuccess: t9 => {
          const {
            title: savedTitle
          } = t9;
          setOptimisticTitle(savedTitle);
        },
        onError: (_error, submittedTitle) => {
          setOptimisticTitle(null);
          setDraftTitle(submittedTitle);
          setIsEditing(true);
        }
      });
    };
    $[7] = cancelEditing;
    $[8] = displayedTitle;
    $[9] = draftTitle;
    $[10] = titleMutation;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const saveTitle = t7;
  let t8;
  if ($[12] !== cancelEditing || $[13] !== saveTitle) {
    t8 = event => {
      if (event.key === "Escape") {
        event.preventDefault();
        ignoreNextBlurRef.current = true;
        cancelEditing();
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        saveTitle();
      }
    };
    $[12] = cancelEditing;
    $[13] = saveTitle;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const onInputKeyDown = t8;
  let t9;
  if ($[15] !== saveTitle) {
    t9 = () => {
      if (ignoreNextBlurRef.current) {
        ignoreNextBlurRef.current = false;
        return;
      }
      saveTitle("blur");
    };
    $[15] = saveTitle;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const onInputBlur = t9;
  const onSaveMouseDown = _temp;
  let t10;
  if ($[17] !== saveTitle) {
    t10 = () => {
      saveTitle();
    };
    $[17] = saveTitle;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const onSaveClick = t10;
  if (!isEditing || readOnly) {
    let t11;
    if ($[19] !== titleLabel || $[20] !== titleTextId) {
      t11 = <span id={titleTextId} className="min-w-0 break-words [overflow-wrap:anywhere]" title={titleLabel}>{titleLabel}</span>;
      $[19] = titleLabel;
      $[20] = titleTextId;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    const titleText = t11;
    let t12;
    if ($[22] !== numberTextId || $[23] !== prNumber) {
      t12 = <PrV2CopyNumberButton number={prNumber} numberTextId={numberTextId} className="relative -left-px text-zinc-400" />;
      $[22] = numberTextId;
      $[23] = prNumber;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t13;
    if ($[25] !== onTitleClick || $[26] !== readOnly || $[27] !== titleText || $[28] !== titleTypeClassName) {
      t13 = readOnly ? titleText : <button type="button" onClick={onTitleClick} aria-label="Edit pull request title" className={cn("min-w-0 flex-1 rounded-sm text-left font-semibold text-foreground", titleTypeClassName, "transition-colors hover:bg-zinc-800/70 focus-visible:bg-zinc-800/70 focus-visible:outline-none")}>{titleText}</button>;
      $[25] = onTitleClick;
      $[26] = readOnly;
      $[27] = titleText;
      $[28] = titleTypeClassName;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    return <h1 className={cn(rowClassName, "items-start")} data-pr-v2-title="true" aria-labelledby={headingLabelledBy}>{t12}{" "}{t13}</h1>;
  }
  const t11 = cn(rowClassName, "items-center");
  const t12 = `#${prNumber}`;
  let t13;
  if ($[30] !== t12) {
    t13 = <span className="relative -left-px shrink-0 text-zinc-400">{t12}</span>;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = event_1 => setDraftTitle(event_1.target.value);
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const t15 = compact ? "text-base md:text-base" : "text-xl md:text-xl";
  let t16;
  if ($[33] !== t15) {
    t16 = cn("h-7 min-w-0 border-none bg-transparent px-0 py-0 font-semibold text-foreground shadow-none focus-visible:ring-0", t15);
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== draftTitle || $[36] !== onInputBlur || $[37] !== onInputKeyDown || $[38] !== t16) {
    t17 = <Input ref={inputRef} value={draftTitle} onChange={t14} onKeyDown={onInputKeyDown} onBlur={onInputBlur} aria-label="Pull request title" className={t16} />;
    $[35] = draftTitle;
    $[36] = onInputBlur;
    $[37] = onInputKeyDown;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Check className="size-3.5" />;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== onSaveClick || $[42] !== onSaveMouseDown) {
    t19 = <button type="button" aria-label="Save title" onMouseDown={onSaveMouseDown} onClick={onSaveClick} className="rounded p-1 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-zinc-600 disabled:cursor-not-allowed disabled:opacity-60">{t18}</button>;
    $[41] = onSaveClick;
    $[42] = onSaveMouseDown;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== t11 || $[45] !== t13 || $[46] !== t17 || $[47] !== t19) {
    t20 = <h1 className={t11} data-pr-v2-title="true">{t13}{t17}{t19}</h1>;
    $[44] = t11;
    $[45] = t13;
    $[46] = t17;
    $[47] = t19;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
}
function _temp(event_0) {
  event_0.preventDefault();
}